import { Fragment, type KeyboardEvent, useEffect, useId, useRef } from 'react';
import { t } from 'i18next';

import TextField from '@/components/atoms/TextField';
import { MenuDivider, MenuHeader, MenuItem } from '@/components/molecules/Menu';
import type { AuthClient } from '@/contexts/auth';

import type { ClientsByStatus } from './types';

import styles from './OptoBreadCrumb.module.scss';

const CLIENT_CHUNK_SIZE = 20;

export interface ClientDropdownItemsProps {
    placeholderText: string;
    clientsByStatus: ClientsByStatus;
    onClientClick: (client: AuthClient) => void;
    onClientNameFilterChange: (value: string) => void;
    selectedClient?: AuthClient;
    isDropdownOpen?: boolean;
    showSearch?: boolean;
}

function chunkClients(clients: AuthClient[]): { items: AuthClient[]; startIndex: number }[] {
    const chunks: { items: AuthClient[]; startIndex: number }[] = [];
    for (let i = 0; i < clients.length; i += CLIENT_CHUNK_SIZE) {
        chunks.push({
            items: clients.slice(i, i + CLIENT_CHUNK_SIZE),
            startIndex: i,
        });
    }
    return chunks;
}

interface ClientGroupProps {
    clients: AuthClient[];
    header?: string;
    keyPrefix: string;
    selectedClient?: AuthClient;
    onClientClick: (client: AuthClient) => void;
}

function ClientGroup(props: ClientGroupProps) {
    const { clients, header, keyPrefix, selectedClient, onClientClick } = props;
    const chunks = chunkClients(clients);

    return (
        <span>
            {header ? <MenuHeader>{header}</MenuHeader> : null}
            {chunks.map(({ items, startIndex }) => (
                <Fragment key={`${keyPrefix}_chunk_${startIndex}`}>
                    <MenuDivider />
                    {items.map((client) => {
                        const isSelected = selectedClient && client.id === selectedClient.id;
                        return (
                            <MenuItem
                                key={`${keyPrefix}_${client.id}`}
                                onClick={() => onClientClick(client)}
                                active={isSelected}>
                                {client.name}
                            </MenuItem>
                        );
                    })}
                </Fragment>
            ))}
        </span>
    );
}

/**
 * The menu answers a typed letter by jumping to the item it starts with, and Tab by
 * closing; in the search field the letters are the field's. Tab and ArrowDown move on to
 * the first client, and Escape still closes the menu.
 */
function handleSearchKey(event: KeyboardEvent<HTMLElement>, list: HTMLElement | null): void {
    if (event.key === 'Escape') return;
    event.stopPropagation();
    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
        const first = list?.querySelector<HTMLElement>('[role="menuitem"]');
        if (first) {
            event.preventDefault();
            first.focus();
        }
    }
}

export default function ClientDropdownItems(props: ClientDropdownItemsProps) {
    const {
        placeholderText,
        clientsByStatus,
        onClientClick,
        onClientNameFilterChange,
        selectedClient,
        isDropdownOpen,
        showSearch = true,
    } = props;
    const listRef = useRef<HTMLDivElement>(null);
    const searchId = useId();
    const noClients = clientsByStatus.active.length === 0 && clientsByStatus.inactive.length === 0;

    // Found by its marker rather than a ref, which the menu item does not hand on.
    useEffect(() => {
        const selectedElement = listRef.current?.querySelector<HTMLElement>('[data-active]');
        if (isDropdownOpen && selectedClient && selectedElement) {
            let scrollContainer: HTMLElement | null = selectedElement.parentElement;

            while (scrollContainer) {
                const style = window.getComputedStyle(scrollContainer);
                if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                    break;
                }
                scrollContainer = scrollContainer.parentElement;
            }

            if (!scrollContainer) {
                // No scrollable ancestor found - this is expected if the list is short
                // and doesn't require scrolling, so we can safely return
                return;
            }

            const containerRect = scrollContainer.getBoundingClientRect();
            const selectedRect = selectedElement.getBoundingClientRect();

            const selectedTop = selectedRect.top - containerRect.top + scrollContainer.scrollTop;
            const containerHeight = scrollContainer.clientHeight;
            const selectedHeight = selectedElement.offsetHeight;

            const targetScrollTop = selectedTop - containerHeight / 2 + selectedHeight / 2;

            scrollContainer.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: 'smooth',
            });
        }
    }, [isDropdownOpen, selectedClient]);

    return (
        <>
            {showSearch ? (
                <legend
                    className={styles.searchInput}
                    onKeyDown={(event) => handleSearchKey(event, listRef.current)}>
                    <label htmlFor={searchId}>{placeholderText}</label>
                    <TextField
                        id={searchId}
                        className={styles.searchField}
                        placeholder={placeholderText}
                        type="text"
                        onChange={(e) => {
                            onClientNameFilterChange(e.target.value);
                        }}
                    />
                </legend>
            ) : null}
            {/* Stays mounted, so a screen reader hears when a search leaves no client. */}
            <output className={styles.noMatches}>
                {showSearch && noClients ? t('molecules.combobox.noMatches') : ''}
            </output>
            <div data-location="sorted" ref={listRef}>
                {clientsByStatus.active.length > 0 && (
                    <ClientGroup
                        clients={clientsByStatus.active}
                        // A lone group needs no label; "Active" only means something next to "Inactive".
                        header={
                            clientsByStatus.inactive.length > 0
                                ? t('common.labels.activeClients')
                                : undefined
                        }
                        keyPrefix="client"
                        selectedClient={selectedClient}
                        onClientClick={onClientClick}
                    />
                )}
                {clientsByStatus.inactive.length > 0 && (
                    <ClientGroup
                        clients={clientsByStatus.inactive}
                        header={t('molecules.optoBreadCrumb.inactiveClients')}
                        keyPrefix="client_inactive"
                        selectedClient={selectedClient}
                        onClientClick={onClientClick}
                    />
                )}
            </div>
        </>
    );
}
